import type { LogRow } from './logFeed'

/** Chat can share an event anchor, but only the event itself is selected. */
export function isSelectedLogRow(row: LogRow, eventIndex: number | null | undefined, offset = 0): boolean {
  return eventIndex != null && !row.chat &&
    eventIndex >= (row.startEventIndex ?? row.eventIndex) + offset &&
    eventIndex <= row.eventIndex + offset
}

export function selectedLogRowIndex(rows: readonly LogRow[], eventIndex: number | null | undefined, offset = 0): number {
  if (eventIndex == null) return -1
  return rows.findIndex((row) => isSelectedLogRow(row, eventIndex, offset))
}

/** A selected historical event must survive the snapshot's normal row cap. */
export function selectedLogWindowSize(limit: number, selectedIndex: number): number {
  return Math.max(limit, selectedIndex + 1)
}

/**
 * Previous / next targets for the snapshot bar's step buttons: the event index
 * a tap on the neighbouring log row would select (`row.eventIndex`), so
 * stepping walks exactly the rows the log shows, never an event folded into a
 * grouped row. `null` at either end of the log.
 *
 * `rows` are the log's event rows (any order); `current` may fall inside a
 * grouped row (a deep link to a raw event), which counts as that row.
 */
export function logStepTargets(
  rows: readonly { eventIndex: number; startEventIndex?: number }[],
  current: number,
): { previous: number | null; next: number | null } {
  const ranges = rows.map((row) => ({ start: row.startEventIndex ?? row.eventIndex, end: row.eventIndex }))
  const here = ranges.find((r) => current >= r.start && current <= r.end) ?? { start: current, end: current }
  let previous: number | null = null
  let next: { start: number; end: number } | null = null
  for (const r of ranges) {
    if (r.end < here.start && (previous === null || r.end > previous)) previous = r.end
    if (r.start > here.end && (next === null || r.start < next.start)) next = r
  }
  return { previous, next: next?.end ?? null }
}
